import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { IHeaderNav } from '@shared/components/header/header.interface';
import { GqlClientService } from '@shared/services/gql/gql-server.service';
import { withResetState } from '@shared/store/features/reset-state.feature';
import { IRequestOptions, withRequestHandling } from '@shared/store/operators/with-request';
import { Logger } from '@shared/utils/logger.util';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { Observable } from 'rxjs';
import { from, map, pipe, switchMap, tap } from 'rxjs';
import type { IAppUser } from './models/user.models';
import { IUser, UserPermission } from './models/user.models';
import { CoreDataService } from './services/core-data.service';

export interface ICoreState {
	register: Record<string, boolean> | null;
	navigation: IHeaderNav[];
	permissions: UserPermission[] | null;
	user: IUser | null;
	appUsers: readonly IAppUser[];
	appUsersLoading: boolean;
	appUsersLoaded: boolean;
}

const initialState: ICoreState = {
	register: {},
	navigation: [],
	permissions: null,
	user: null,
	appUsers: [],
	appUsersLoading: false,
	appUsersLoaded: false
};

export const CoreStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withResetState(initialState),
	withMethods(store => {
		const dataService = inject(CoreDataService);
		const gql = inject(GqlClientService);

		const fetchAppUsers = rxMethod<IRequestOptions | void>(
			pipe(
				withRequestHandling<IRequestOptions | void>({
					shouldFetch: () => !store.appUsersLoading() && !store.appUsersLoaded(),
					setLoading: (loading: boolean) => patchState(store, state => ({ ...state, appUsersLoading: loading }))
				}),
				switchMap(() =>
					dataService.fetchAppUsers().pipe(
						tapResponse({
							next: rows =>
								patchState(store, state => ({
									...state,
									appUsers: rows,
									appUsersLoading: false,
									appUsersLoaded: true
								})),
							error: () =>
								patchState(store, state => ({
									...state,
									appUsers: [],
									appUsersLoading: false,
									appUsersLoaded: false
								}))
						})
					)
				)
			)
		);

		const insertUserAccessRecords = rxMethod<IRequestOptions | void>(
			pipe(
				switchMap(() =>
					dataService.insertUserAccessRecord(store.user()?.profile ?? '').pipe(
						tapResponse({
							next: () => {},
							error: error => console.warn('insertUserAccessRecords error: ', error)
						}),
						tap(() => fetchAppUsers())
					)
				)
			)
		);

		return {
			setRegister(register: Record<string, boolean>): void {
				patchState(store, state => ({ ...state, register: { ...state.register, ...register } }));
			},
			setNavigation(navigation: ICoreState['navigation']): void {
				patchState(store, state => ({ ...state, navigation }));
			},
			hasSomePermission(requiredPermissions: UserPermission[] = []): boolean {
				return requiredPermissions.length > 0 && !dataService.isLocalEnv()
					? requiredPermissions.some(permission => store.permissions()?.includes(permission))
					: true;
			},
			hasEveryPermissions(requiredPermissions: UserPermission[] = []): boolean {
				return requiredPermissions.length > 0 && !dataService.isLocalEnv()
					? requiredPermissions.every(permission => store.permissions()?.includes(permission))
					: true;
			},
			getJwt(): Observable<string | undefined> {
				return from(fetchAuthSession()).pipe(map(session => session.tokens?.idToken?.toString()));
			},
			signIn(): void {
				dataService.signIn();
			},
			signOut(): void {
				dataService.signOut();
			},
			async eraseSession(): Promise<void> {
				try {
					await Promise.allSettled([gql.client.client.clearStore(), gql.pip.client.clearStore(), gql.pricing.client.clearStore()]);
				} catch (error) {
					Logger.err('Core: failed to clear Apollo caches', error);
				}
				try {
					localStorage.clear();
				} catch (error) {
					Logger.err('Core: failed to clear localStorage', error);
				}
				try {
					sessionStorage.clear();
				} catch (error) {
					Logger.err('Core: failed to clear sessionStorage', error);
				}
				dataService.signOut();
			},
			init: rxMethod<void>(
				pipe(
					switchMap(() =>
						dataService.loadProfile().pipe(
							tapResponse({
								next: user =>
									patchState(store, state => ({
										...state,
										user,
										register: { ...state.register, userDataReady: true }
									})),
								error: () => patchState(store, state => ({ ...state, register: { ...state.register, userDataReady: false } }))
							}),
							tap(() => insertUserAccessRecords())
						)
					)
				)
			),
			fetchPermissions: rxMethod<void>(
				pipe(
					switchMap(() =>
						dataService.fetchPermissions(store.user()?.profile).pipe(
							tapResponse({
								next: permissions => patchState(store, state => ({ ...state, permissions })),
								error: () => {}
							})
						)
					)
				)
			),
			fetchAppUsers
		};
	})
);
